import { useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useTranslation, useLocale } from "@/lib/i18n";
import {
  MaritimeArea,
  useMaritimeAreasQuery,
} from "@/features/dashboard/components/warnings/maritime-areas.queries";
import {
  MaritimeWarning,
  selectMaritimeWarnings,
  useMaritimeWarningsQuery,
} from "@/features/dashboard/components/warnings/maritime-warnings.queries";
import {
  getAreaName,
  getWarningDescription,
} from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import { MARITIME_FORECAST_DOMAINS } from "@/features/dashboard/components/warnings/maritime-forecast.domains";
import {
  MARITIME_FORECAST_PERIOD_KEYS,
  MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS,
  MaritimeForecastShortRangePeriodKey,
} from "@/features/dashboard/components/warnings/maritime-forecast-period-keys";
import { useMaritimeForecastQuery } from "@/features/dashboard/components/warnings/maritime-forecast.queries";
import {
  findRange,
  findValue,
  formatCloudWeatherRowText,
  formatVisibilityRowText,
  formatWindGustRowText,
} from "@/features/dashboard/components/warnings/maritime-forecast-phenomena.utils";
import {
  formatForecastIssued,
  formatForecastFooterLines,
} from "@/features/dashboard/components/warnings/maritime-forecast-publication-format.utils";
import {
  MaritimeForecastWidgetConfiguration,
  MaritimeForecastAreasInner,
  resolveMaritimeForecastAreaIds,
} from "@/lib/api";

import MaritimeForecastRenderer, {
  ForecastAreaRow,
  ForecastDay1Temp,
  ForecastDay2Temp,
  ForecastPeriodRows,
  ForecastWarningEntry,
  ForecastWaterTemp,
  MaritimeForecastRendererProps,
} from "./MaritimeForecastRenderer";

interface MaritimeForecastProps {
  id: string;
  data: unknown;
  configuration: MaritimeForecastWidgetConfiguration;
}

const AMS = "Europe/Amsterdam";

const formatPeriodHeader = (start: string, end: string): string =>
  `${formatInTimeZone(new Date(start), AMS, "HH:mm")}-${formatInTimeZone(new Date(end), AMS, "HH:mm")} uur:`;

const MaritimeForecast = ({ configuration }: MaritimeForecastProps) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const formatAmsterdamDayName = (iso: string): string =>
    formatInTimeZone(new Date(iso), AMS, "EEEE", { locale: getDateFnsLocale(locale) });

  const formatSunTime = (iso: string): string =>
    formatInTimeZone(new Date(iso), AMS, "HH:mm");

  const areaIds = useMemo(
    () => resolveMaritimeForecastAreaIds(configuration),
    [configuration],
  );

  const forecastQuery = useMaritimeForecastQuery(MARITIME_FORECAST_DOMAINS.coastal);
  const areasQuery = useMaritimeAreasQuery();
  const warningsQuery = useMaritimeWarningsQuery();

  const rendererProps = useMemo((): MaritimeForecastRendererProps | null => {
    const forecast = forecastQuery.data;
    const allAreas = areasQuery.data;
    const allWarnings = selectMaritimeWarnings(warningsQuery.data);
    if (!forecast || !allAreas || !warningsQuery.data) return null;

    // Build area name map and filter to selected areas (in config order)
    const areaNameMap = new Map(
      allAreas.map((a: MaritimeArea) => [a.id, getAreaName(a, locale)]),
    );

    const selectedAreas = areaIds
      .map((id) => ({
        id,
        name: areaNameMap.get(id) ?? id,
        forecastArea: forecast.areas.find((a) => a.area_id === id) ?? null,
      }))
      .filter((a) => a.forecastArea !== null) as Array<{
      id: string;
      name: string;
      forecastArea: MaritimeForecastAreasInner;
    }>;

    // Warnings per selected area (highest level wins)
    const warningByArea = new Map<string, MaritimeWarning>();
    for (const w of allWarnings) {
      if (!areaIds.some((id) => id === w.area_id)) continue;
      const existing = warningByArea.get(w.area_id);
      if (!existing || w.level > existing.level) warningByArea.set(w.area_id, w);
    }

    const warnings: ForecastWarningEntry[] = selectedAreas.map(({ id, name }) => {
      const warning = warningByArea.get(id);
      return {
        areaName: name,
        description: warning ? getWarningDescription(warning, "nl") : undefined,
      };
    });

    const hasWarnings = warningByArea.size > 0;

    // Synopsis
    const synopsis = forecast.synopsis.nl ?? "";

    // Period headers (Amsterdam local time)
    const periodHeaders: string[] = MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS.map((key) => {
      const def = forecast.periods[key];
      if (!def || def.type !== "time_range") return key;
      return formatPeriodHeader(def.start, def.end);
    });

    // Wind + Gust section
    const buildWindGustRows = (
      periodKey: MaritimeForecastShortRangePeriodKey,
    ): ForecastAreaRow[] =>
      selectedAreas.map(({ name, forecastArea }) => {
        const entries = forecastArea.periods[periodKey] ?? [];
        return { areaName: name, text: formatWindGustRowText(entries) };
      });

    const windGustPeriods: ForecastPeriodRows[] =
      MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS.map((key, i) => ({
        header: periodHeaders[i],
        rows: buildWindGustRows(key),
      }));

    // Cloud + Weather section
    const buildCloudWeatherRows = (
      periodKey: MaritimeForecastShortRangePeriodKey,
    ): ForecastAreaRow[] =>
      selectedAreas.map(({ name, forecastArea }) => {
        const entries = forecastArea.periods[periodKey] ?? [];
        return { areaName: name, text: formatCloudWeatherRowText(entries) };
      });

    const cloudWeatherPeriods: ForecastPeriodRows[] =
      MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS.map((key, i) => ({
        header: periodHeaders[i],
        rows: buildCloudWeatherRows(key),
      }));

    // Visibility section (uses additional_message)
    const buildVisibilityRows = (
      periodKey: MaritimeForecastShortRangePeriodKey,
    ): ForecastAreaRow[] =>
      selectedAreas.map(({ name, forecastArea }) => {
        const entries = forecastArea.periods[periodKey] ?? [];
        return { areaName: name, text: formatVisibilityRowText(entries) };
      });

    const visibilityPeriods: ForecastPeriodRows[] =
      MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS.map((key, i) => ({
        header: periodHeaders[i],
        rows: buildVisibilityRows(key),
      }));

    // Temperature + Sun section (day_1 and day_2)
    const day1Temps: ForecastDay1Temp[] = [];
    for (const { name, forecastArea } of selectedAreas) {
      const entries = forecastArea.periods[MARITIME_FORECAST_PERIOD_KEYS.day1] ?? [];
      const temp = findRange(entries, "air_temperature");
      if (!temp) continue;
      const sun = forecastArea.sun_times;
      day1Temps.push({
        areaName: name,
        min: temp.min,
        max: temp.max,
        sunrise: sun ? formatSunTime(sun.sunrise_at) : undefined,
        sunset: sun ? formatSunTime(sun.sunset_at) : undefined,
      });
    }

    const day2Temps: ForecastDay2Temp[] = [];
    for (const { name, forecastArea } of selectedAreas) {
      const entries = forecastArea.periods[MARITIME_FORECAST_PERIOD_KEYS.day2] ?? [];
      const temp = findRange(entries, "air_temperature");
      if (!temp) continue;
      day2Temps.push({ areaName: name, min: temp.min, max: temp.max });
    }

    // Water temperature (day_1 only)
    const waterTemps: ForecastWaterTemp[] = selectedAreas
      .map(({ name, forecastArea }) => {
        const entries = forecastArea.periods[MARITIME_FORECAST_PERIOD_KEYS.day1] ?? [];
        const val = findValue(entries, "water_temperature");
        return val !== null ? { areaName: name, value: val } : null;
      })
      .filter((v): v is ForecastWaterTemp => v !== null);

    // Day name labels for temperature headers
    const day1Period = forecast.periods[MARITIME_FORECAST_PERIOD_KEYS.day1];
    const day1Label =
      day1Period?.type === "day"
        ? formatAmsterdamDayName(day1Period.date + "T00:00:00")
        : t("widgets.maritime_forecast.day_fallback", { n: 1 });
    const day2Period = forecast.periods[MARITIME_FORECAST_PERIOD_KEYS.day2];
    const day2Label =
      day2Period?.type === "day"
        ? formatAmsterdamDayName(day2Period.date + "T00:00:00")
        : t("widgets.maritime_forecast.day_fallback", { n: 2 });

    return {
      issuedLine: forecast.published_at
        ? formatForecastIssued(forecast.published_at)
        : undefined,
      footerLines: forecast.next_forecast_at
        ? formatForecastFooterLines(forecast.next_forecast_at)
        : undefined,
      synopsis,
      warnings,
      noWarningsText: t("widgets.maritime_forecast.no_warnings"),
      hasWarnings,
      windGustSectionHeader: t("widgets.maritime_forecast.wind_section_header"),
      windGustPeriods,
      cloudWeatherSectionHeader: t("widgets.maritime_forecast.cloud_section_header"),
      cloudWeatherPeriods,
      visibilitySectionHeader: t("widgets.maritime_forecast.visibility_section_header"),
      visibilityPeriods,
      day1TempHeader: t("widgets.maritime_forecast.day1_temp_header", {
        day: day1Label,
      }),
      day1Temps,
      day2TempHeader: t("widgets.maritime_forecast.day2_temp_header", {
        day: day2Label,
      }),
      day2Temps,
      waterTempHeader: t("widgets.maritime_forecast.water_temp_header"),
      waterTemps,
    };
  }, [forecastQuery.data, areasQuery.data, warningsQuery.data, areaIds, t, locale]);

  if (forecastQuery.isLoading || areasQuery.isLoading || warningsQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (!rendererProps) return null;

  return <MaritimeForecastRenderer {...rendererProps} />;
};

export default MaritimeForecast;
